import { Box, Skeleton, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { useAppSelector } from "../app/hooks"
import { selectMeIsReady } from "../features/me/meSlice"
import SaLayout, { SaMenuGroups, SaMenuItems } from "./SaLayout"
import { UnstyledLink } from "./UnstyledLink"

export const SaHomePage: FC = () => {
  const { t } = useTranslation()
  const isReady = useAppSelector(selectMeIsReady)

  if (!isReady) {
    return (
      <SaLayout selectedMenuItemLabelKey="lockers">
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 6 },
          }}
        >
          <Stack spacing={2}>
            <Skeleton variant="text" width={120} height={16} />
            <Skeleton variant="rounded" width="45%" height={42} />
            <Skeleton variant="rounded" width="70%" height={22} />
          </Stack>
          <Box
            sx={{
              mt: 6,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 2,
            }}
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={108} />
            ))}
          </Box>
        </Box>
      </SaLayout>
    )
  }

  const visibleItems = SaMenuItems.filter((i) => i.visible)
  const itemByKey = new Map(visibleItems.map((i) => [i.labelKey, i]))
  const groupedKeys = new Set(SaMenuGroups.flatMap((g) => g.labelKeys))
  const ungrouped = visibleItems.filter((i) => !groupedKeys.has(i.labelKey))

  const groups = SaMenuGroups.map((g) => ({
    titleKey: g.titleKey,
    items: g.labelKeys
      .map((k) => itemByKey.get(k))
      .filter((x): x is (typeof visibleItems)[number] => Boolean(x)),
  })).filter((g) => g.items.length > 0)

  return (
    <SaLayout>
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 3, md: 6 },
          py: { xs: 4, md: 6 },
        }}
      >
        {/* Header */}
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <Typography
            sx={{
              color: "primary.main",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              fontSize: "0.72rem",
            }}
          >
            {t("adminApp")}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              mt: 0.75,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "text.primary",
              lineHeight: 1.15,
            }}
          >
            {t("adminApp")}
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              color: "text.secondary",
              maxWidth: 640,
              lineHeight: 1.6,
              fontSize: "0.975rem",
            }}
          >
            {t("adminAppDescription")}
          </Typography>
        </Box>

        {/* Grouped card sections */}
        <Stack spacing={{ xs: 4, md: 5 }}>
          {groups.map((group) => (
            <Box key={group.titleKey}>
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  mb: 1.5,
                  pb: 1,
                  borderBottom: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.08)",
                }}
              >
                {t(group.titleKey)}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  gap: 2,
                  alignItems: "stretch",
                  "& > a": {
                    display: "block",
                    height: "100%",
                    textDecoration: "none",
                    color: "inherit",
                  },
                }}
              >
                {group.items.map((item) => (
                  <UnstyledLink
                    to={item.url}
                    key={item.labelKey}
                    disabled={item.disabled}
                  >
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: "white",
                        border: "1px solid",
                        borderColor: "rgba(0, 0, 0, 0.08)",
                        height: "100%",
                        minHeight: 132,
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        cursor: item.disabled ? "not-allowed" : "pointer",
                        opacity: item.disabled ? 0.55 : 1,
                        transition:
                          "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
                        "&:hover": item.disabled
                          ? {}
                          : {
                              borderColor: "primary.main",
                              boxShadow: "0 6px 24px rgba(35, 66, 133, 0.09)",
                              transform: "translateY(-2px)",
                            },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          backgroundImage:
                            "linear-gradient(135deg, rgba(35, 66, 133, 0.12), rgba(148, 210, 194, 0.14))",
                          color: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1.75,
                          "& svg": { fontSize: "1.25rem" },
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          color: "text.primary",
                          letterSpacing: "-0.005em",
                          lineHeight: 1.3,
                        }}
                      >
                        {t(item.labelKey)}
                      </Typography>
                    </Box>
                  </UnstyledLink>
                ))}
              </Box>
            </Box>
          ))}

          {ungrouped.length > 0 && (
            <Box>
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  mb: 1.5,
                  pb: 1,
                  borderBottom: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.08)",
                }}
              >
                {t("adminApp")}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  gap: 2,
                  alignItems: "stretch",
                  "& > a": {
                    display: "block",
                    height: "100%",
                    textDecoration: "none",
                    color: "inherit",
                  },
                }}
              >
                {ungrouped.map((item) => (
                  <UnstyledLink
                    to={item.url}
                    key={item.labelKey}
                    disabled={item.disabled}
                  >
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: "white",
                        border: "1px solid",
                        borderColor: "rgba(0, 0, 0, 0.08)",
                        height: "100%",
                        minHeight: 132,
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        cursor: item.disabled ? "not-allowed" : "pointer",
                        opacity: item.disabled ? 0.55 : 1,
                        transition:
                          "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
                        "&:hover": item.disabled
                          ? {}
                          : {
                              borderColor: "primary.main",
                              boxShadow: "0 6px 24px rgba(35, 66, 133, 0.09)",
                              transform: "translateY(-2px)",
                            },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          backgroundImage:
                            "linear-gradient(135deg, rgba(35, 66, 133, 0.12), rgba(148, 210, 194, 0.14))",
                          color: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1.75,
                          "& svg": { fontSize: "1.25rem" },
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          color: "text.primary",
                          letterSpacing: "-0.005em",
                          lineHeight: 1.3,
                        }}
                      >
                        {t(item.labelKey)}
                      </Typography>
                    </Box>
                  </UnstyledLink>
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      </Box>
    </SaLayout>
  )
}

export default SaHomePage
